import React from 'react';
import isNil from 'lodash/isNil';
import cn from 'classnames';
import { PredictedPaiParams, PublishedParams, PredictedIktosParams } from '@Pages/RetroSynthesis/constants';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import { isIktosPredictiveRetrosynthesisEnabled, isPaiPredictiveRetrosynthesisEnabled } from '../../predictiveUtils';
import { paramsToString, buildIktosParameters, buildPaiParameters } from './projectParametersUtils';
import IntermediatesParameter from './IntermediatesParameter';

import './ProjectParametersGridItem.scss';

export function ParamItem({ paramName, value, paramType, params, isPredicted, step }) {
  let text = paramsToString(paramName, value, isPredicted, params);
  let isHighlighted = false;
  if (isPredicted && step) {
    isHighlighted = params?.highlightedParams && params.highlightedParams.includes(paramName);
    if (isHighlighted) text = `<b>*${text}</b>`;
  }

  return !isNil(value) ? <InnerHTML className={`param ${paramType}-param param--${paramName}`} tag="div" html={text} /> : null;
}

ParamItem.propTypes = {
  paramName: PropTypes.string,
  value: PropTypes.any,
  paramType: PropTypes.string,
  params: PropTypes.object,
  isPredicted: PropTypes.bool,
  step: PropTypes.number,
};

function isIktosSubstructureParam(paramName) {
  return paramName === PredictedIktosParams.INCLUDE_SUBSTRUCTURES || paramName === PredictedIktosParams.EXCLUDE_SUBSTRUCTURES;
}

function filterIktosParam(paramName, predictedParametersUpdated) {
  if (paramName === PredictedIktosParams.INCLUDE_SUBSTRUCTURES) return true;
  if (paramName === PredictedIktosParams.EXCLUDE_SUBSTRUCTURES) return !isNil(predictedParametersUpdated[paramName]);
  return !isNil(predictedParametersUpdated[paramName]);
}

function renderIktosParamItem(paramName, predictedParametersUpdated, step) {
  if (isIktosSubstructureParam(paramName)) {
    return (
      <li key={`predicted-param-${paramName}`}>
        <IntermediatesParameter smiles={predictedParametersUpdated[paramName]} paramName={paramName} />
      </li>
    );
  }
  return (
    <li key={`predicted-param-${paramName}`}>
      <ParamItem
        paramName={paramName}
        value={predictedParametersUpdated[paramName]}
        paramType="predicted"
        params={predictedParametersUpdated}
        isPredicted
        step={step}
      />
    </li>
  );
}

function preparePredictedParameters(predictedParameters, predictiveIktosEnabled, predictivePAIEnabled) {
  if (predictiveIktosEnabled && Object.keys(predictedParameters).length) {
    return buildIktosParameters({ ...predictedParameters });
  }
  if (predictivePAIEnabled) {
    return buildPaiParameters({ ...predictedParameters });
  }
  return { ...predictedParameters };
}

export function ProjectParametersGridItem({ publishedParameters = {}, predictedParameters = {}, step }) {
  const predictiveIktosEnabled = isIktosPredictiveRetrosynthesisEnabled();
  const predictivePAIEnabled = !predictiveIktosEnabled && isPaiPredictiveRetrosynthesisEnabled();
  const predictedParametersUpdated = preparePredictedParameters(predictedParameters, predictiveIktosEnabled, predictivePAIEnabled);

  return (
    <div
      className={cn('project-parameters-grid-item', {
        'project-parameters-grid-item__one-column': !predictivePAIEnabled && !predictiveIktosEnabled,
      })}>
      {predictivePAIEnabled && (
        <ul className="params-bunch" aria-label="Predicted parameters">
          {PredictedPaiParams?.allTypes
            .filter((paramName) => !isNil(predictedParametersUpdated[paramName]))
            .map((paramName) => (
              <li key={`predicted-param-${paramName}`}>
                <ParamItem
                  paramName={paramName}
                  value={predictedParametersUpdated[paramName]}
                  paramType="predicted"
                  params={predictedParametersUpdated}
                  isPredicted
                  step={step}
                />
              </li>
            ))}
        </ul>
      )}
      {predictiveIktosEnabled && (
        <ul className="params-bunch" aria-label="Predicted parameters">
          {Object.keys(predictedParametersUpdated).length > 0 &&
            PredictedIktosParams?.allTypes
              .filter((paramName) => filterIktosParam(paramName, predictedParametersUpdated))
              .map((paramName) => renderIktosParamItem(paramName, predictedParametersUpdated, step))}
        </ul>
      )}
      <ul className="params-bunch" aria-label="Published parameters">
        {PublishedParams?.allTypes
          .filter((paramName) => !isNil(publishedParameters[paramName]))
          .map((paramName) => (
            <li key={`published-param-${paramName}`}>
              <ParamItem
                paramName={paramName}
                value={publishedParameters[paramName]}
                paramType="published"
                params={publishedParameters}
                isPredicted={false}
              />
            </li>
          ))}
      </ul>
    </div>
  );
}

export default ProjectParametersGridItem;
